/**
 * Was kostet eine Fotobox — beantwortet aus dem eigenen Katalog.
 *
 * "Preis / Kosten" ist mit knapp 30.000 Impressionen in 90 Tagen das größte
 * Themenfeld der Domain, und die Seite hatte dazu bisher einen Satz ohne eine
 * einzige Zahl. Der Wettbewerb schreibt an dieser Stelle Fließtext ("die
 * Preise variieren je nach Ausstattung"). Wir haben 121 echte Pakete — das
 * ist der einzige Vorteil, den niemand kopieren kann.
 *
 * Deshalb entsteht jede Zahl hier aus den Daten und keine steht im Text.
 * Kommt ein Anbieter dazu oder ändert einen Preis, stimmt die Seite am
 * nächsten Tag von selbst.
 *
 * Der Median statt des Durchschnitts: Ein Fotobus für 1.000 € zieht einen
 * Mittelwert nach oben, bis er niemandem mehr etwas sagt.
 */

// Unter dieser Zahl an Paketen wird eine Gruppe nicht ausgewiesen. Ein
// "Median" aus drei Paketen ist keine Marktaussage, sondern Zufall.
const MINDESTGROESSE = 5;

/** 1000 → "1.000". Ohne Punkt liest sich der größte Wert wie ein Tippfehler. */
export function euro(betrag) {
  return new Intl.NumberFormat("de-DE").format(Math.round(betrag || 0));
}

/**
 * 3.8 → "3,80". Für Beträge, bei denen die Cent-Stelle mitgemeint ist:
 * "3,8 €" liest sich wie ein abgeschnittener Preis, nicht wie ein Betrag.
 */
export function euroDezimal(betrag) {
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(betrag || 0);
}

export function median(zahlen) {
  const s = [...zahlen].sort((a, b) => a - b);
  if (!s.length) return 0;
  const mitte = Math.floor(s.length / 2);
  return s.length % 2 ? s[mitte] : Math.round((s[mitte - 1] + s[mitte]) / 2);
}

/** Alle Paketpreise einer Box: Grundpreis plus Paketaufschlag. */
export function paketPreise(box) {
  const grundpreis =
    typeof box?.PhotoboothPrice === "number" ? box.PhotoboothPrice : 0;
  return (box?.Packages || [])
    .map((paket) => ({
      preis:
        grundpreis +
        (typeof paket.PackagePrice === "number" ? paket.PackagePrice : 0),
      druck: Boolean(paket.Print),
      flatrate: Boolean(paket.SpecialPrintFlatrate),
      kapazitaet: paket.Printer?.PaperStorageSize || 0,
      bauform: box.PhotoboothType || null,
      versand: Boolean(box.PostDelivery),
      vorOrt: Boolean(box.SelfDelivery || box.CustomerPickUp),
    }))
    .filter((p) => p.preis > 0);
}

function gruppe(pakete, label, bedingung) {
  const treffer = pakete.filter(bedingung);
  if (treffer.length < MINDESTGROESSE) return null;
  const preise = treffer.map((p) => p.preis);
  return {
    label,
    anzahl: treffer.length,
    min: Math.min(...preise),
    median: median(preise),
    max: Math.max(...preise),
  };
}

export function preisAnalyse(photobooths = []) {
  const boxen = photobooths.filter(Boolean);
  const pakete = boxen.flatMap(paketPreise);

  if (!pakete.length) return null;

  const alle = pakete.map((p) => p.preis);

  const nachDruck = [
    gruppe(pakete, "Nur digital, ohne Drucker", (p) => !p.druck),
    gruppe(pakete, "Mit Druck, festes Kontingent", (p) => p.druck && !p.flatrate),
    gruppe(pakete, "Mit Druck-Flatrate", (p) => p.flatrate),
  ].filter(Boolean);

  const nachLieferung = [
    gruppe(pakete, "Versand-Fotobox", (p) => p.versand),
    gruppe(pakete, "Anbieter vor Ort", (p) => p.vorOrt && !p.versand),
  ].filter(Boolean);

  const bauformen = [...new Set(pakete.map((p) => p.bauform).filter(Boolean))];
  const nachBauform = bauformen
    .map((form) => gruppe(pakete, form, (p) => p.bauform === form))
    .filter(Boolean)
    .sort((a, b) => a.median - b.median);

  const nachKapazitaet = [
    gruppe(
      pakete,
      "bis 200 Bilder",
      (p) => p.druck && p.kapazitaet > 0 && p.kapazitaet <= 200
    ),
    gruppe(
      pakete,
      "200 bis 400 Bilder",
      (p) => p.druck && p.kapazitaet > 200 && p.kapazitaet <= 400
    ),
    gruppe(pakete, "über 400 Bilder", (p) => p.druck && p.kapazitaet > 400),
  ].filter(Boolean);

  const versandboxen = boxen.filter((b) => b.PostDelivery);

  return {
    anzahlBoxen: boxen.length,
    anzahlPakete: pakete.length,
    gesamt: {
      min: Math.min(...alle),
      median: median(alle),
      max: Math.max(...alle),
    },
    nachDruck,
    nachLieferung,
    nachBauform,
    nachKapazitaet,
    versand: {
      gesamt: versandboxen.length,
      kostenlos: versandboxen.filter((b) => !b.PostDeliveryCosts).length,
    },
    umbuchung: {
      gesamt: boxen.length,
      kostenlos: boxen.filter((b) => b.FreeSchedule).length,
    },
    vorOrt: boxen.filter((b) => b.SelfDelivery || b.CustomerPickUp).length,
  };
}
